import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';

function RegisterPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [searchParams] = useSearchParams();
  const { register: registerUser, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Set referral code from URL params
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setValue('referralCode', refCode);
    }
  }, [searchParams, setValue]);

  const referralCode = watch('referralCode');

  const onSubmit = async (data) => {
    try {
      const result = await registerUser({
        email: data.email,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
        countryCode: data.countryCode,
        referralCode: data.referralCode || undefined
      });
      
      if (result.success) {
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      // Error handling is done in AuthContext
    }
  };

  const countries = [
    { code: 'TR', name: 'Türkiye', cpm: '$0.80' },
    { code: 'US', name: 'United States', cpm: '$2.50' },
    { code: 'GB', name: 'United Kingdom', cpm: '$2.20' },
    { code: 'DE', name: 'Germany', cmp: '$2.00' },
    { code: 'FR', name: 'France', cpm: '$1.80' },
    { code: 'CA', name: 'Canada', cpm: '$2.00' },
    { code: 'AU', name: 'Australia', cpm: '$1.90' },
    { code: 'BR', name: 'Brazil', cpm: '$0.60' },
    { code: 'IN', name: 'India', cpm: '$0.40' },
  ];

  if (isAuthenticated) {
    return null; // Prevent flash before redirect
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-2xl">K</span>
            </div>
          </div>
          <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
            Ücretsiz Hesap Oluşturun
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Zaten hesabınız var mı?{' '}
            <Link
              to="/login"
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              Giriş yapın
            </Link>
          </p>
        </div>

        {/* Referral Info */}
        {referralCode && (
          <div className="card bg-green-50 border-green-200">
            <div className="card-body">
              <div className="flex items-center">
                <UserGroupIcon className="w-5 h-5 text-green-600 mr-2" />
                <div>
                  <p className="text-sm font-medium text-green-800">
                    Referans Kodu: {referralCode}
                  </p>
                  <p className="text-xs text-green-600">
                    Bir arkadaşınız sizi davet etti! 🎉
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="card">
          <div className="card-body">
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                    Ad
                  </label>
                  <input
                    id="firstName"
                    type="text"
                    className={`form-input ${errors.firstName ? 'error' : ''}`}
                    placeholder="Adınız"
                    {...register('firstName', {
                      required: 'Ad gereklidir',
                      minLength: {
                        value: 2,
                        message: 'Ad en az 2 karakter olmalıdır'
                      },
                      maxLength: {
                        value: 50,
                        message: 'Ad en fazla 50 karakter olabilir'
                      }
                    })}
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-error-600">{errors.firstName.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                    Soyad
                  </label>
                  <input
                    id="lastName"
                    type="text"
                    className={`form-input ${errors.lastName ? 'error' : ''}`}
                    placeholder="Soyadınız"
                    {...register('lastName', {
                      required: 'Soyad gereklidir',
                      minLength: {
                        value: 2,
                        message: 'Soyad en az 2 karakter olmalıdır'
                      },
                      maxLength: {
                        value: 50,
                        message: 'Soyad en fazla 50 karakter olabilir'
                      }
                    })}
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-error-600">{errors.lastName.message}</p>
                  )}
                </div>
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  E-posta Adresi
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  className={`form-input ${errors.email ? 'error' : ''}`}
                  placeholder="ornek@email.com"
                  {...register('email', {
                    required: 'E-posta adresi gereklidir',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Geçerli bir e-posta adresi girin'
                    }
                  })}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-error-600">{errors.email.message}</p>
                )}
              </div>

              {/* Country */}
              <div>
                <label htmlFor="countryCode" className="block text-sm font-medium text-gray-700 mb-2">
                  Ülke (Kazanç Oranları İçin)
                </label>
                <select
                  id="countryCode"
                  className={`form-select ${errors.countryCode ? 'error' : ''}`}
                  {...register('countryCode', {
                    required: 'Ülke seçimi gereklidir'
                  })}
                >
                  <option value="">Ülkenizi seçin</option>
                  {countries.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.name} ({country.cpm}/1000 görüntüleme)
                    </option>
                  ))}
                </select>
                {errors.countryCode && (
                  <p className="mt-1 text-sm text-error-600">{errors.countryCode.message}</p>
                )}
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Şifre
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    className={`form-input pr-10 ${errors.password ? 'error' : ''}`}
                    placeholder="Güçlü bir şifre oluşturun"
                    {...register('password', {
                      required: 'Şifre gereklidir',
                      minLength: {
                        value: 6,
                        message: 'Şifre en az 6 karakter olmalıdır'
                      },
                      pattern: {
                        value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                        message: 'Şifre en az 1 büyük harf, 1 küçük harf ve 1 rakam içermelidir'
                      }
                    })}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-error-600">{errors.password.message}</p>
                )}
              </div>

              {/* Referral Code */}
              <div>
                <label htmlFor="referralCode" className="block text-sm font-medium text-gray-700 mb-2">
                  Referans Kodu (Opsiyonel)
                </label>
                <input
                  id="referralCode"
                  type="text"
                  className="form-input"
                  placeholder="Referans kodunuz varsa girin"
                  {...register('referralCode', {
                    pattern: {
                      value: /^[A-Z0-9]{6,20}$/,
                      message: 'Geçersiz referans kodu formatı'
                    }
                  })}
                />
                {errors.referralCode && (
                  <p className="mt-1 text-sm text-error-600">{errors.referralCode.message}</p>
                )}
                {referralCode && (
                  <p className="mt-1 text-sm text-green-600">
                    ✓ Referans kodu uygulandı - Komisyon kazancı alacaksınız!
                  </p>
                )}
              </div>

              {/* Terms */}
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="terms"
                    type="checkbox"
                    className="form-checkbox"
                    {...register('terms', {
                      required: 'Kullanım şartlarını kabul etmelisiniz'
                    })}
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="terms" className="text-gray-700">
                    <Link to="/page/terms" className="text-primary-600 hover:text-primary-500">
                      Kullanım Şartları
                    </Link>{' '}
                    ve{' '}
                    <Link to="/page/privacy" className="text-primary-600 hover:text-primary-500">
                      Gizlilik Politikası
                    </Link>
                    'nı okudım ve kabul ediyorum
                  </label>
                  {errors.terms && (
                    <p className="mt-1 text-error-600">{errors.terms.message}</p>
                  )}
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn btn-primary btn-lg"
                >
                  {isSubmitting ? (
                    <>
                      <div className="loading-spinner w-5 h-5 mr-2"></div>
                      Hesap oluşturuluyor...
                    </>
                  ) : (
                    'Hesap Oluştur'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Benefits */}
        <div className="card bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
          <div className="card-body">
            <h3 className="font-semibold text-gray-900 mb-3">Üyelik Avantajları</h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-center">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                Sınırsız link kısaltma
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                Ülke bazlı yüksek kazanç oranları
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                Detaylı istatistikler ve raporlar
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                PayPal ile hızlı ödeme
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                Referans sistemi ile ek kazanç
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RegisterPage;